import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

export function useOutlookCalendar(connected, weekStart, enabled = true) {
  const [events, setEvents] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [status, setStatus] = useState(connected ? 'active' : 'disconnected');
  const [refreshing, setRefreshing] = useState(false);
  const latestRequest = useRef(0);

  const load = useCallback(
    async (force = false) => {
      const requestId = ++latestRequest.current;
      if (!connected) {
        setEvents([]);
        setLastUpdated(null);
        setStatus('disconnected');
        setRefreshing(false);
        return;
      }
      if (!enabled) return;
      setRefreshing(true);
      try {
        const query = new URLSearchParams({ weekStart });
        if (force) query.set('force', '1');
        const result = await api(`/outlook/calendar?${query}`);
        if (requestId !== latestRequest.current) return;
        setEvents(result.events || []);
        setLastUpdated(result.fetchedAt || null);
        setStatus(result.status || 'active');
      } catch (error) {
        if (requestId === latestRequest.current) throw error;
      } finally {
        if (requestId === latestRequest.current) setRefreshing(false);
      }
    },
    [connected, enabled, weekStart],
  );

  useEffect(() => {
    // Clear immediately on disconnect, even while fetching is disabled
    // (e.g. disconnecting from Settings), so stale events never linger
    // until the next visit to the calendar.
    if (!connected) {
      latestRequest.current += 1;
      setEvents([]);
      setLastUpdated(null);
      setStatus('disconnected');
      setRefreshing(false);
      return;
    }
    if (!enabled) {
      latestRequest.current += 1;
      setRefreshing(false);
      return;
    }
    load().catch(() => setEvents([]));
  }, [connected, enabled, load]);

  useEffect(() => {
    if (!connected || !enabled || status === 'needs_reconnect')
      return undefined;
    function refreshWhenVisible() {
      if (!document.hidden) load().catch(() => {});
    }
    function onVisibilityChange() {
      if (!document.hidden) refreshWhenVisible();
    }
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = window.setInterval(() => {
      if (!document.hidden) load().catch(() => {});
    }, 60_000);
    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(interval);
    };
  }, [connected, enabled, load, status]);

  return { events, lastUpdated, status, refreshing, refresh: () => load(true) };
}
